import {Injectable, OnDestroy} from '@angular/core';
import {forkJoin, Observable, of, Subject} from "rxjs";
import {first, map, takeUntil} from "rxjs/operators";
import {CustomEventsService} from "../../services/custom-events.service";
import {ConstantProperty} from '../../_models/constant-property';
import {ColumnFormatsConfigDict} from "../table-column-menu/table-column-menu.component";
import {ConditionalFormattingService} from '../../services/conditional-formatting.service';
import {ListResponse} from '../../services/api/response-types';
import {getColIds, getPropertyIds, getRelatedComponentTypeIds} from '../../tables/custom-events-table/utils';
import {SeriesDataTableConfig} from "../../_typing/config/series-data-table";
import {ConstantPropertyDataService} from "../../data/constant-property-data.service";
import {EventConfigColumn} from "../../_typing/config/config-column";
import {ComponentType} from "../../_models/component-type";
import {ComponentTypeDataService} from "../../data/component-type-data.service";

@Injectable()
export class ConditionalFormattingFormService implements OnDestroy {
    private readonly onDestroy = new Subject<void>();
    private constant_properties: ConstantProperty[];
    private cp_dict: { [key: string]: ConstantProperty } = {};

    private default_component_events_cols = ['name', 'start_time', 'end_time'];
    private default_custom_events_cols = ['start', 'end', 'comment', 'duration', 'source', 'destination']

    named_formats: ColumnFormatsConfigDict;
    operator_dict = {
        'auto': 'Auto',
        'empty': 'Is Empty',
        'not_empty': 'Is Not Empty',
        '=': 'Equals',
        '!=': 'Not Equal to',
        '>': 'Greater than',
        '<': 'Less than',
        '>=': 'Greater than or Equal to',
        '<=': 'Less than or Equal to',
        '<>': 'Is between',
        '><': 'Is outside of',
        'case_includes': 'Contains/matches, Case sensitive',
        '!_case_includes': 'Does not contain/match, Case sensitive',
        'includes': 'Contains/matches, Case insensitive',
        '!_includes': 'Does not contain/match, Case insensitive'
    }

    constructor(private customEventsService: CustomEventsService,
                private formattingService: ConditionalFormattingService,
                private propertyData: ConstantPropertyDataService,
                private componentTypeData: ComponentTypeDataService) {
        this.named_formats = formattingService.named_formats;

    }

    getColumns(config, key): Observable<{ name: string, id: string, type: string }[]> {
        if (key === 'selected_component_type') {
            return this.getComponentTypeColumns(config);
        }
        if (key === 'event_types') {
            return this.getEventTypeColumns(config)
        }
        if (key === 'process_list') {
            return this.getSeriesDataColumns(config);
        }
    }

    private getComponentTypeColumns(config) {
        let selected_cols = getColIds(config.selected_cols?.component_type);
        if (!selected_cols || selected_cols.length < 1) return of([]);
        const default_cols = this.getSelectedDefaultTypeColumns(this.default_component_events_cols, selected_cols);
        return this.addColumnsObservablePipe(forkJoin([this.getComponentTypeProperties(config), this.getComponentTypeComponentTypes(config)]), default_cols, config.selected_cols.component_type);
    }

    private getSelectedDefaultTypeColumns(defaultList, selected_cols) {
        return defaultList.filter(col => selected_cols.includes(col)).map(col => {
            return {name: col, id: col, type: 'attribute'}
        })
    }

    getSeriesDataColumns(config: SeriesDataTableConfig): Observable<{ name: string, id: string, type: string }[]> {
        let series = [];
        Object.keys(config.process_series_map).forEach(processId => {
            series = series.concat(config.process_series_map[processId]?.map(series => {
                return {name: series.attributes?.name, id: series.id, type: 'series'}
            }))
        });
        return of(series);
    }

    private addColumnsObservablePipe($obs: Observable<any>, default_cols, selectedColumns?: EventConfigColumn[]) {
        return $obs.pipe(
            first(),
            map(([cpColumns, relatedColumns]) => {
                let columns = cpColumns?.data || [];
                let relatedNameDict = {};
                relatedColumns?.data?.forEach(c => relatedNameDict[c.id] = c.attributes.name);

                columns = default_cols.concat(columns.map(d => {
                let relatedName = '';
                    const configColumn = selectedColumns?.find(col => col.id === d.id);
                    if (configColumn?.related_type) {
                        relatedName = configColumn.title || `${relatedNameDict[configColumn.related_type_id]}: ${d.attributes.name}`
                    }
                    return {
                        name: d.attributes.name,
                        id: d.id || d.attributes.name,
                        type: d.type,
                        related_name: relatedName,
                        related_type: configColumn?.related_type,
                        related_type_id: configColumn?.related_type_id
                    }
                }));
                return columns
            }),
            takeUntil(this.onDestroy))
    }

    private getComponentTypeProperties(config): Observable<ListResponse<ConstantProperty>> {
        const propIds = getPropertyIds(config.selected_cols?.component_type);
        if (!propIds?.length) return of(null);
        return this.propertyData.getConstantProperties(propIds);
    }

    private getComponentTypeComponentTypes(config): Observable<ListResponse<ComponentType>> {
        const ctIds = getRelatedComponentTypeIds(config.selected_cols?.component_type);
        if (!ctIds?.length) return of(null);
        return this.componentTypeData.getComponentTypes(ctIds);
    }

    private getEventTypeColumns(config) {
        let selected_cols = getColIds(config.columns);
        if (!selected_cols || selected_cols.length < 1) return of([]);
        const default_cols = this.getSelectedDefaultTypeColumns(this.default_custom_events_cols, selected_cols);
        return this.addColumnsObservablePipe(forkJoin([this.getEventTypeProperties(config), of(null)]), default_cols);
    }

    private getEventTypeProperties(config): Observable<ListResponse<ConstantProperty>> {
        return this.customEventsService.getConstantPropertiesByEventTypeIds(config.event_types.map(e => e.id), getPropertyIds(config.columns));
    }

    ngOnDestroy(): void {
        this.onDestroy.next();
        this.onDestroy.unsubscribe();
    }
}
